import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import RouteChangeListener from "src/components/RouteChangeListener";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import WithLayout from "./components/WithLayout";
import SysAdminLayout from "./layouts/SysAdminLayout";
import LoginLayout, {
  SoneraLaunchLoginLayout,
  MergerNoticeLoginLayout,
} from "./layouts/LoginLayout";
import ComplianceGuideLayout from "./layouts/ComplianceGuideLayout";

import AccountUsage from "./pages/Reports/AccountUsage";
import PoliciesSent from "./pages/Reports/PoliciesSent";
import InvestigatePhoneNumber from "./pages/Reports/InvestigatePhoneNumber";
import ReassignedDashboard from "./pages/Reports/ReassignedDashboard/ReassignedDashboard";
import ReassignedSessions from "./pages/Reports/ReassignedSessions/Index";
import ScrubLogs from "./pages/Reports/ScrubLogs/Index";
import ExpansionReport from "./pages/Reports/Expansion/Index";
import Users from "./pages/Users/Users";
import AddUser from "./pages/Users/AddUser";
import Tcl from "./components/Tcl";
import NotFound from "src/components/NotFound";
import Error from "src/components/Error";
import Version from "./pages/Version";
import CGIntro from "./pages/ComplianceGuide/Index";
import CGRegulatoryGuide from "./pages/ComplianceGuide/RegulatoryGuide/Index";
import CGCharts from "./pages/ComplianceGuide/Charts/Index";
import CGRegulatoryWatch from "./pages/ComplianceGuide/RegulatoryWatch/Index";
import CGDNCMap from "./pages/ComplianceGuide/DNCMap/Index";
import CGStatutes from "./pages/ComplianceGuide/Statutes/Index";
import CGFaqIndex from "./pages/ComplianceGuide/FAQ/Index";
import CGComplianceWizard from "./pages/ComplianceGuide/ComplianceWizard/Index";
import CGGlossary from "./pages/ComplianceGuide/Glossary/Index";
import CGConsentChart from "./pages/ComplianceGuide/ConsentChart/Index";
import CGLitigatorSearchNew from "./pages/ComplianceGuide/LitigatorSearchNew/Index";
import CGLitigatorScrub from "./pages/ComplianceGuide/LitigatorScrub/Index";
import CGSubmitLitigator from "./pages/ComplianceGuide/SubmitLitigator/Index";
import CGOtherTools from "./pages/ComplianceGuide/OtherTools/Index";
import CGFTCComplaintSearch from "./pages/ComplianceGuide/FTCComplaintSearch/Index";
import CGAIAssistant from "./pages/ComplianceGuide/AIAssistant/Index";

// Stable wrapper components for AIAssistant routes.
// Inline arrow functions (e.g. `Component={() => <CGAIAssistant ... />}`) create
// a new component identity on every parent render, which causes WithLayout to
// unmount/remount AIAssistant — wiping `messages`, `inputMessage`, `threadId`,
// etc. That manifested as: streaming answer reverting to the welcome message,
// and the input field clearing while a user was typing.
const CGAIAssistantPrivate = () => <CGAIAssistant isPublic={false} />;
const CGAIAssistantPublic = () => <CGAIAssistant isPublic={true} />;
import CGSignup from "./pages/ComplianceGuide/Signup/Index";
import { SignupWizard } from "./components/SignupWizard/SignupWizard";
import UploadListMain from "./pages/Main/Main";
import useInactivity from "./hooks/useInactivity";
import { logout, selectAuth } from "./features/authSlice";
import { useLogoutUserMutation } from "./services/authApi";
import { useAppDispatch } from "./app/hooks";
import NewPassword from "./pages/NewPassword";
import SelfRegister from "./pages/SelfRegister";
import OAuthConsent from "./pages/OAuth/Consent";
import ScrubWarnings from "./pages/Main/UploadList/ScrubWarnings";
import AdminAccountsMain from "./pages/Admin/Accounts/AdminAccountsMain";
import AdminIntelliScrub from "./pages/Admin/Settings/IntelliScrub";
import AdminAccount from "./pages/Admin/Settings/Account";
import LitigatorScrubSettings from "./pages/Admin/Settings/LitigatorScrubSettings";
import Home from "src/components/Home";
import AccountsIsland from "./components/Sysadmin-React-Islands/AccountsIsland";
import Profile from "./pages/Profile";
import VerifyEmail from "./pages/VerifyEmail";
import RecordPayment from "./pages/Sysadmin/RecordPayment";
import RecordAdjustment from "./pages/Sysadmin/RecordAdjustment";
import AdHocDialer from "./pages/Sysadmin/AdHocDialer";
import TNRMReputationScan from "./pages/Sysadmin/AdvancedRemediation/TNRMReputationScan";
import HiyaReputationScan from "./pages/Sysadmin/AdvancedRemediation/HiyaReputationScan";

import { useAppSelector } from "src/app/hooks";
import PublicLayout from "./layouts/PublicLayout";

/* Load application insight */
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { hasValidInstrumentationKey } from "./utils/appInsightsConfig";
import { createBrowserHistory } from "history";
const browserHistory = createBrowserHistory({});
var reactPlugin = new ReactPlugin();

// In dev, leave VITE_APP_INSIGHTS_CONN_STRING env variable blank
// so we don't populate App Insights with dev usage.
const appInsightsConnString = import.meta.env.VITE_APP_INSIGHTS_CONN_STRING;

var appInsights = new ApplicationInsights({
  config: {
    connectionString: appInsightsConnString,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
  },
});

if (hasValidInstrumentationKey(appInsightsConnString)) {
  appInsights.loadAppInsights();
} else if (appInsightsConnString) {
  console.warn(
    "App Insights disabled: VITE_APP_INSIGHTS_CONN_STRING has no valid instrumentation key.",
  );
}

import ManageEnterprise from "./pages/LogoCall/ManageEnterprise";
import DisplayRecords from "./pages/LogoCall/DisplayRecords";
import LogoCallLayout from "./layouts/LogoCallLayout";
import Reporting from "./pages/LogoCall/Reporting";

import TrustCallLayout from "./layouts/TrustCallLayout";
import TrustCallDashboard from "./pages/TrustCall/Dashboard";
import TrustCallActivePhones from "./pages/TrustCall/ActivePhones";
import TrustCallArchivedPhones from "./pages/TrustCall/ArchivedPhones";
import TrustCallAdHocScan from "./pages/TrustCall/AdHocScan";
import NotificationSettings from "./pages/TrustCall/components/NotificationSettings";
import WebhookSettings from "./pages/TrustCall/components/WebhookSettings";
import LegalEntityList from "./pages/TrustCall/components/LegalEntityList";
import SysadminTools from "./pages/TrustCall/components/SysadminTools";
import TrustCallHelpDocViewer from "./pages/TrustCall/HelpDocViewer";
import TicketDetails from "./pages/Help/TicketDetails";
import NewTicket from "./pages/Help/NewTicket";
import ListTickets from "./pages/Help/ListTickets";
import Docs from "./pages/Help/Docs";
import AccountsList from "./components/Sysadmin-React-Islands/AccountsList";
import AccountReminderList from "./components/Sysadmin-React-Islands/AccountReminderList";
import PayInvoiceOnline from "./pages/PayInvoiceOnline";
import ACHInfo from "./pages/ACHInfo/Index";
import Payments from "./components/Sysadmin-React-Islands/Payments";
import Adjustments from "./components/Sysadmin-React-Islands/Adjustments";
import AccountsReceivable from "./pages/Sysadmin/AccountsReceivable";
import AISessions from "./pages/Sysadmin/AI/AISessions";
import AIInteractions from "./pages/Sysadmin/AI/AIInteractions";
import SystemPrompts from "./pages/Sysadmin/AI/SystemPrompts";
import SystemPromptEditor from "./pages/Sysadmin/AI/SystemPromptEditor";
import TrustCallOneTimeScanJobs from "./pages/Sysadmin/TrustCallOneTimeScanJobs";
import RemediationCosts from "./pages/Sysadmin/RemediationCosts";
import LeadLookup from "./pages/Sales/LeadLookup";
import SalesDashboard from "./pages/Sales/SalesDashboard";
import SetTermDate from "./pages/Sysadmin/SetTermDate";
import WarmupJobs from "./pages/Sysadmin/WarmupJobs";
import SimFarmDashboard from "./pages/Sysadmin/SimFarmDashboard";
import SimFarmGrid from "./pages/Sysadmin/SimFarmGrid";
import SKUManagement from "./pages/Sysadmin/SKUManagement";
import BrandedCallingBilling from "./pages/Sysadmin/BrandedCallingBilling";
import ReassignedPools from "./pages/Sysadmin/ReassignedPools";
import GhlDashboard from "./pages/Sysadmin/GoHighLevel/Dashboard";
import GhlAgencies from "./pages/Sysadmin/GoHighLevel/Agencies";
import GhlAgencyDetail from "./pages/Sysadmin/GoHighLevel/AgencyDetail";
import GhlLocations from "./pages/Sysadmin/GoHighLevel/Locations";
import GhlSubAccountDetail from "./pages/Sysadmin/GoHighLevel/SubAccountDetail";
import ProductPricingModels from "./pages/Sysadmin/ProductPricingModels";
import EditProductPricingModel from "./pages/Sysadmin/EditProductPricingModel";

// Thin wrappers so WithLayout (which renders <Component /> with no props) can
// forward the required prop to components that need one.
const TrustCallPhonesAdd = () => <TrustCallActivePhones section="add" />;
const TrustCallPhonesBulkAdd = () => <TrustCallActivePhones section="bulk-add" />;
const TrustCallPhonesBulkRemove = () => <TrustCallActivePhones section="bulk-remove" />;
const TrustCallUserGuide = () => <TrustCallHelpDocViewer docKey="user-guide" />;
const TrustCallApiDocs = () => <TrustCallHelpDocViewer docKey="api-docs" />;
// SysadminOnly must render INSIDE WithLayout so that SiteLayout → PrivateRoute
// can fire the auth check first.  Wrapping WithLayout from the outside blocks
// PrivateRoute from ever mounting, leaving isSysadmin stuck at null and the
// page permanently blank on hard refresh.
const SysadminLegalEntityList = () => <SysadminOnly><LegalEntityList /></SysadminOnly>;
const SysadminSysadminTools = () => <SysadminOnly><SysadminTools /></SysadminOnly>;

// Entry-point route for resellers coming from Call Deliverability. Sets a
// sessionStorage flag so SiteLayout hides the top nav for the lifetime of
// the popup tab, then redirects to the normal TrustCall route.
const ResellerTrustCallEntry: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem("hideNav", "reseller");
    const path = location.pathname.replace("/trustcall/reseller", "/trustcall") || "/trustcall/dashboard";
    navigate(path + location.search + location.hash, { replace: true });
  }, [location, navigate]);

  return null;
};

// Sysadmin route guard — redirects non-sysadmins to the default settings page.
// Previously enforced inside Settings.tsx; preserved here as defense-in-depth for
// when the TrustCall portal opens to non-sysadmin users.
// While auth is loading (isSysadmin is null), render nothing so deep links
// aren't lost to a premature redirect.
const SysadminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSysadmin } = useAppSelector(selectAuth);
  if (isSysadmin === null) return null;
  if (!isSysadmin) return <Navigate to="/trustcall/settings/notifications" replace />;
  return <>{children}</>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const isDeveloper = false;

  // Get from the Redux store if the user is authenticated
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  // Absolute session expiry as ISO 8601 UTC, set by login + every keepalive.
  // Drives the modal countdown via wall-clock math so the timer does not drift
  // under browser-tab throttling. Server also enforces the DEMO-account
  // 2-hour rule; the client no longer encodes that policy.
  const expiresAt = useAppSelector((state) => state.auth.expiresAt);

  // Get means to logout user
  const dispatch = useAppDispatch();
  const [logoutUser] = useLogoutUserMutation();

  const [showInactivityModal, setShowInactivityModal] = useState(false);

  // useInactivity runs a 1-second wall-clock watchdog against expiresAt.
  // isInactive flips true once remaining time drops below the modal window.
  // isExpired flips true once the deadline has passed; forces logout even if
  // the modal has been dismissed (e.g. after a transient keepalive failure).
  const { isInactive, isExpired, refreshSession } = useInactivity();

  const loginURL = `/login`;

  // function to logout user
  const handleLogout = useCallback(
    (reason: string) => {
      console.log(`User wants to logout. Reason: ${reason}`);
      logoutUser({});
      dispatch(logout());
      setShowInactivityModal(false);

      let url = `${loginURL}?reason=${reason}`;
      if (window && window.top) {
        window.top.location.href = url;
      } else {
        window.location.href = url;
      }
    },
    [dispatch, logoutUser, loginURL],
  );

  // function to stay logged in
  const handleStayLoggedIn = useCallback(async () => {
    // Immediately hide the modal and stop the countdown to prevent
    // the countdown from expiring while the API call is in-flight,
    // which would cause a "Failed to fetch" error (JAVASCRIPT-REACT-99)
    setShowInactivityModal(false);
    const result = await refreshSession();
    if (result === "unauthorized") {
      // Server definitively says the session is gone. Force logout.
      handleLogout("error");
    }
    // "transient": network/5xx — leave the modal hidden, don't log out. The
    // watchdog still holds the original expiresAt; if it truly passes, the
    // hard-expiry branch in the modal countdown will fire handleLogout.
  }, [refreshSession, handleLogout]);

  // Show the countdown modal when the watchdog flips isInactive.
  // Also hide it when isInactive flips back to false (e.g. activity event
  // fired a keepalive and pushed expiry further out).
  useEffect(() => {
    if (isAuthenticated) {
      setShowInactivityModal(isInactive);
    }
  }, [isInactive, isAuthenticated]);

  // Hard-expiry enforcement. Fires even if the modal is not currently showing,
  // which matters on transient keepalive failure after "Stay signed in": the
  // modal gets dismissed but the session may still run out.
  useEffect(() => {
    if (isExpired && isAuthenticated) {
      handleLogout("expired");
    }
  }, [isExpired, isAuthenticated, handleLogout]);

  // Redirect to https if not already
  // We don't do this in our reverse proxy because
  // I am concerned that would break any API requests
  // that come to us today as http
  useEffect(() => {
    if (window.location.protocol !== "https:") {
      window.location.href = `https://${window.location.hostname}${window.location.pathname}${window.location.search}`;
    }
  }, []);

  // Seconds remaining until hard expiry, computed from wall-clock each tick.
  // Ticking is only active while the modal is showing. Unlike a decrementing
  // counter, this stays accurate if the tab is throttled or the watchdog
  // missed a few ticks.
  const [modalCountdown, setModalCountdown] = useState(60);
  useEffect(() => {
    if (!showInactivityModal) {
      setModalCountdown(60);
      return;
    }
    const tick = () => {
      if (!expiresAt) {
        setModalCountdown(0);
        return;
      }
      const remainingMs = new Date(expiresAt).getTime() - Date.now();
      const secsLeft = Math.max(0, Math.floor(remainingMs / 1000));
      setModalCountdown(secsLeft);
      if (secsLeft <= 0) handleLogout("expired");
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [showInactivityModal, expiresAt, handleLogout]);

  return (
    <AppInsightsErrorBoundary
      appInsights={reactPlugin}
      onError={() => <h1>Something went wrong. Please try again later.</h1>}
    >
      <>
        {showInactivityModal && (
          <div className="modal d-block" tabIndex={-1}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {"Your session is about to expire!"}
                  </h5>
                </div>
                <div className="modal-body d-flex">
                  <i className="h1 text-danger bi bi-exclamation-triangle pe-3"></i>
                  <div>
                    You will be logged out in <span>{modalCountdown}</span>{" "}
                    seconds.
                    <br />
                    Do you want to stay signed in?
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleStayLoggedIn}
                  >
                    Yes, Keep me signed in
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-dismiss="modal"
                    onClick={() => handleLogout("user")}
                  >
                    No, Sign me out
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showInactivityModal && (
          <div className="modal-backdrop fade show"></div>
        )}
        <div className="App">
          <HelmetProvider>
            <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <RouteChangeListener>
                <Toaster richColors position="top-right" />
                <Routes>
                  <Route
                    path="/"
                    element={
                      isDeveloper ? (
                        <Navigate to="/main/upload-list/:campaignId" replace />
                      ) : (
                        //<Navigate to="/compliance-guide/intro" replace />
                        <Navigate to="/login" replace />
                        //<ExternalRedirect to="https://classic.dncscrub.com/index.adp?afterLoginPage=https%3a%2f%2fapp.dncscrub.com%2fcompliance-guide%2fintro" />
                      )
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      <WithLayout
                        Component={Login}
                        Layout={LoginLayout}
                        PageTitle="Login"
                      />
                    }
                  />
                  {/* Preview of the post-rebrand login: "DNCScrub by Sonera"
                      lockup + the Sonera gradient (same as dnc.sonera.dev).
                      This is what customers will see once the Sonera name goes
                      live (July 6). Same working login form as /login. */}
                  <Route
                    path="/login-on-7-13"
                    element={
                      <WithLayout
                        Component={Login}
                        Layout={SoneraLaunchLoginLayout}
                        PageTitle="Login"
                      />
                    }
                  />
                  {/* Preview of the 7/28 merger-day login: "DNCScrub by Sonera"
                      lockup + merger notice, original theme colors unchanged.
                      /login itself switches to this look when MERGER_LOCKUP_LIVE
                      is flipped in LoginLayout.tsx. Same working login form. */}
                  <Route
                    path="/login-on-7-28"
                    element={
                      <WithLayout
                        Component={Login}
                        Layout={MergerNoticeLoginLayout}
                        PageTitle="Login"
                      />
                    }
                  />
                  <Route
                    path="/login/reset-your-password"
                    element={
                      <WithLayout
                        Component={ResetPassword}
                        Layout={LoginLayout}
                        PageTitle="Reset Password"
                      />
                    }
                  />
                  <Route
                    path="/login/self-register"
                    element={
                      <WithLayout
                        Component={SelfRegister}
                        Layout={LoginLayout}
                        PageTitle="SelfRegister"
                      />
                    }
                  />
                  <Route
                    path="/login/new-password"
                    element={
                      <WithLayout
                        Component={NewPassword}
                        Layout={LoginLayout}
                        PageTitle="Choose New Password"
                      />
                    }
                  />
                  <Route
                    path="/oauth/consent"
                    element={
                      <WithLayout
                        Component={OAuthConsent}
                        Layout={LoginLayout}
                        PageTitle="Link Account"
                      />
                    }
                  />
                  <Route
                    path="/home"
                    element={<WithLayout Component={Home} />}
                  />
                  <Route
                    path="/profile"
                    element={
                      <WithLayout Component={Profile} PageTitle="My Profile" />
                    }
                  />
                  <Route
                    path="/verify-email"
                    element={
                      <WithLayout
                        Component={VerifyEmail}
                        PageTitle="Verify Email"
                      />
                    }
                  />
                  {/* Main */}
                  <Route
                    path="/main/upload-list/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Do Not Call Upload"
                      />
                    }
                  />
                  <Route
                    path="/main/reassigned-scrub/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Reassigned Upload"
                      />
                    }
                  />
                  <Route
                    path="/main/project-settings/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Project Settings"
                      />
                    }
                  />
                  <Route
                    path="/main/campaign-settings/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Campaign Settings"
                      />
                    }
                  />
                  {/* Abhi: Tcl App stores adp links in the database. 
                Until full migration we are adding this so that both apps can coexist in harmony */}
                  <Route
                    path="/m-dncdnm-db.adp"
                    element={<Navigate replace to="/main/internal-dnc" />}
                  />
                  <Route
                    path="/main/internal-dnc/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Internal DNC"
                      />
                    }
                  />
                  <Route
                    path="/main/ebr-master/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="EBR Master"
                      />
                    }
                  />
                  <Route
                    path="/main/dnc-policy/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="DNC Policy"
                      />
                    }
                  />
                  <Route
                    path="/main/cross-account-info/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Cross Account Info"
                      />
                    }
                  />
                  {/* Abhi: Tcl App stores adp links in the database. 
                Until full migration we are adding this so that both apps can coexist in harmony */}
                  <Route
                    path="/m-send.adp"
                    element={<Navigate replace to="/main/send-policy" />}
                  />
                  <Route
                    path="/main/send-policy/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Send Policy"
                      />
                    }
                  />
                  <Route
                    path="/main/exemption-master/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Exemption Master"
                      />
                    }
                  />
                  <Route
                    path="/main/scrub-options/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Scrub Options"
                      />
                    }
                  />
                  <Route
                    path="/main/training-master/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Training Master"
                      />
                    }
                  />
                  {/* Abhi: Tcl App stores adp links in the database. 
                Until full migration we are adding this so that both apps can coexist in harmony */}
                  <Route
                    path="/m-quick-scrub.adp"
                    element={<Navigate replace to="/main/quickscrub" />}
                  />
                  <Route
                    path="/main/quickscrub/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="QuickScrub"
                      />
                    }
                  />
                  <Route
                    path="/main/quickscrubresults/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="QuickScrub Results"
                      />
                    }
                  />
                  <Route
                    path="/main/cdr-analysis/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="CDR Analysis"
                      />
                    }
                  />
                  <Route
                    path="/main/no-call-holidays/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="No Call Holidays"
                      />
                    }
                  />
                  <Route
                    path="/main/calling-times/:campaignId?"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Calling Times"
                      />
                    }
                  />
                  {/* Abhi: Tcl App stores adp links in the database.
                Until full migration we are adding this so that both apps can coexist in harmony */}
                  <Route
                    path="/m-training.adp"
                    element={<Navigate replace to="/main/training" />}
                  />
                  <Route
                    path="/main/training"
                    element={
                      <WithLayout
                        Component={UploadListMain}
                        PageTitle="Training Master"
                      />
                    }
                  />
                  <Route
                    path="/warnings"
                    element={
                      <WithLayout
                        Component={ScrubWarnings}
                        PageTitle="Warnings"
                      />
                    }
                  />
                  {/* Reports */}
                  <Route
                    path="/reports/policies-sent"
                    element={
                      <WithLayout
                        Component={PoliciesSent}
                        PageTitle="Reports - Policies Sent"
                      />
                    }
                  />
                  <Route
                    path="/reports/investigate-phone-number"
                    element={
                      <WithLayout
                        Component={InvestigatePhoneNumber}
                        PageTitle="Investigate Phone Number"
                      />
                    }
                  />
                  <Route
                    path="/reports/reassigned-dashboard"
                    element={
                      <WithLayout
                        Component={ReassignedDashboard}
                        PageTitle="Reports - Reassigned Dashboard"
                      />
                    }
                  />
                  <Route
                    path="/reports/reassigned-sessions"
                    element={
                      <WithLayout
                        Component={ReassignedSessions}
                        PageTitle="Reassigned Sessions"
                      />
                    }
                  />
                  <Route
                    path="/reports/scrub-logs"
                    element={
                      <WithLayout
                        Component={ScrubLogs}
                        PageTitle="Scrub Logs"
                      />
                    }
                  />
                  <Route
                    path="/reports/account-usage"
                    element={
                      <WithLayout
                        Component={AccountUsage}
                        PageTitle="Account Usage"
                      />
                    }
                  />
                  {/* Users */}
                  <Route
                    path="/users"
                    element={<WithLayout Component={Users} />}
                  />
                  <Route
                    path="/add-user"
                    element={<WithLayout Component={AddUser} />}
                  />
                  {/* Tcl app links */}
                  <Route
                    path="/t/:slug"
                    element={<WithLayout Component={Tcl} />}
                  />
                  {/* Compliance Guide */}
                  <Route
                    path="/compliance-guide/intro"
                    element={
                      <WithLayout
                        Component={CGIntro}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Intro"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/regulatory-guide"
                    element={
                      <WithLayout
                        Component={CGRegulatoryGuide}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Regulatory Guide"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/regulatory-charts"
                    element={
                      <WithLayout
                        Component={CGCharts}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Charts"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/regulatory-watch"
                    element={
                      <WithLayout
                        Component={CGRegulatoryWatch}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Regulatory Watch"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/dnc-map"
                    element={
                      <WithLayout
                        Component={CGDNCMap}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide DNC Map"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/statutes"
                    element={
                      <WithLayout
                        Component={CGStatutes}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Statutes"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/faq"
                    element={
                      <WithLayout
                        Component={CGFaqIndex}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide FAQ"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/compliance-wizard"
                    element={
                      <WithLayout
                        Component={CGComplianceWizard}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Wizard"
                      />
                    }
                  />
                  {/* <Route
              path="/compliance-guide/exemption-master"
              element={<WithLayout Component={CGExemptionMaster} Layout={ComplianceGuideLayout} />}
            /> */}
                  <Route
                    path="/compliance-guide/glossary"
                    element={
                      <WithLayout
                        Component={CGGlossary}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Glossary"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/consent-chart"
                    element={
                      <WithLayout
                        Component={CGConsentChart}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Consent Chart"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/litigator-research"
                    element={
                      <WithLayout
                        Component={CGLitigatorSearchNew}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Litigator Research"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/litigator-scrub"
                    element={
                      <WithLayout
                        Component={CGLitigatorScrub}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Litigator Scrub"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/ftc-complaint-search"
                    element={
                      <WithLayout
                        Component={CGFTCComplaintSearch}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide FTC Complaint Search"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/submit-litigator"
                    element={
                      <WithLayout
                        Component={CGSubmitLitigator}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Submit Litigator"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/other-tools"
                    element={
                      <WithLayout
                        Component={CGOtherTools}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="Compliance Guide Other Tools"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/ai-assistant"
                    element={
                      <WithLayout
                        Component={CGAIAssistantPrivate}
                        SubLayout={ComplianceGuideLayout}
                        PageTitle="AI Compliance Assistant"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/ai-assistant/public"
                    element={
                      <WithLayout
                        Component={CGAIAssistantPublic}
                        Layout={PublicLayout}
                        PageTitle="AI Compliance Assistant"
                      />
                    }
                  />
                  <Route
                    path="/compliance-guide/signup"
                    element={
                      <WithLayout
                        Component={CGSignup}
                        Layout={PublicLayout}
                        PageTitle="Sign Up - Compliance Guide"
                      />
                    }
                  />
                  {/* Admin (don't conflate with sysadmin!) */}
                  <Route
                    path="/admin/contact-info"
                    element={
                      <WithLayout
                        Component={AdminAccountsMain}
                        PageTitle="Admin Contact Info"
                      />
                    }
                  />
                  {/* Sysadmin components to embed in Tcl app via React Island pattern*/}
                  <Route
                    path="/sysadmin/island-proof-of-concept"
                    element={<AccountsIsland />}
                  />
                  <Route
                    path="/sysadmin/account-list"
                    element={
                      <SysAdminLayout
                        Component={AccountsList}
                        PageTitle="Accounts"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/payments"
                    element={
                      <SysAdminLayout
                        Component={Payments}
                        PageTitle="Payments"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/account-reminders"
                    element={
                      <SysAdminLayout
                        Component={AccountReminderList}
                        PageTitle="Account Reminders"
                      />
                    }
                  />
                  {/* Example URL https://spa.dncscrub.com/sysadmin/payment/5253 */}
                  <Route
                    path="/sysadmin/payment/:accountNumId"
                    element={
                      <SysAdminLayout
                        Component={RecordPayment}
                        PageTitle="Record Payment"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/adjustments"
                    element={
                      <SysAdminLayout
                        Component={Adjustments}
                        PageTitle="Adjustments"
                      />
                    }
                  />
                  {/* Example URL https://spa.dncscrub.com/sysadmin/adjustment/5253 */}
                  <Route
                    path="/sysadmin/adjustment/:accountNumId"
                    element={
                      <SysAdminLayout
                        Component={RecordAdjustment}
                        PageTitle="Record Adjustment"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/accounts-receivable"
                    element={
                      <SysAdminLayout
                        Component={AccountsReceivable}
                        PageTitle="Accounts Receivable Aging"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/ad-hoc-dialer/:phone?"
                    element={
                      <SysAdminLayout
                        Component={AdHocDialer}
                        PageTitle="Ad Hoc Dialer"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/tnrm-scan/:phone?"
                    element={
                      <SysAdminLayout
                        Component={TNRMReputationScan}
                        PageTitle="TNRM Reputation Scan"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/hiya-scan/:phone?"
                    element={
                      <SysAdminLayout
                        Component={HiyaReputationScan}
                        PageTitle="Hiya Reputation Scan"
                      />
                    }
                  />
                  {/* AI Sessions & Prompts Management */}
                  <Route
                    path="/sysadmin/ai/sessions"
                    element={
                      <SysAdminLayout
                        Component={AISessions}
                        PageTitle="AI Sessions"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/ai/sessions/:id"
                    element={
                      <SysAdminLayout
                        Component={AIInteractions}
                        PageTitle="AI Session Interactions"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/ai/prompts"
                    element={
                      <SysAdminLayout
                        Component={SystemPrompts}
                        PageTitle="System Prompts"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/ai/prompts/:id"
                    element={
                      <SysAdminLayout
                        Component={SystemPromptEditor}
                        PageTitle="Edit System Prompt"
                      />
                    }
                  />
                  {/* GoHighLevel admin console */}
                  <Route
                    path="/sysadmin/gohighlevel"
                    element={<Navigate to="/sysadmin/gohighlevel/dashboard" replace />}
                  />
                  <Route
                    path="/sysadmin/gohighlevel/dashboard"
                    element={
                      <SysAdminLayout Component={GhlDashboard} PageTitle="GoHighLevel Dashboard" />
                    }
                  />
                  <Route
                    path="/sysadmin/gohighlevel/agencies"
                    element={
                      <SysAdminLayout Component={GhlAgencies} PageTitle="GoHighLevel Agencies" />
                    }
                  />
                  <Route
                    path="/sysadmin/gohighlevel/agencies/:companyId"
                    element={
                      <SysAdminLayout Component={GhlAgencyDetail} PageTitle="GoHighLevel Agency" />
                    }
                  />
                  <Route
                    path="/sysadmin/gohighlevel/locations"
                    element={
                      <SysAdminLayout Component={GhlLocations} PageTitle="GoHighLevel Locations" />
                    }
                  />
                  <Route
                    path="/sysadmin/gohighlevel/locations/:locationId"
                    element={
                      <SysAdminLayout Component={GhlSubAccountDetail} PageTitle="GoHighLevel Subaccount" />
                    }
                  />
                  {/* TrustCall One Time Scan Jobs */}
                  <Route
                    path="/sysadmin/trustcall-onetimescan-jobs/:jobId?"
                    element={
                      <SysAdminLayout
                        Component={TrustCallOneTimeScanJobs}
                        PageTitle="TrustCall One Time Scan Jobs"
                      />
                    }
                  />
                  {/* Remediation Costs Dashboard */}
                  <Route
                    path="/sysadmin/remediation-costs"
                    element={
                      <SysAdminLayout
                        Component={RemediationCosts}
                        PageTitle="Remediation Costs"
                      />
                    }
                  />
                  {/* SKU Management */}
                  <Route
                    path="/sysadmin/sku-management"
                    element={
                      <SysAdminLayout
                        Component={SKUManagement}
                        PageTitle="SKU Management"
                      />
                    }
                  />
                  {/* Product Pricing Models */}
                  <Route
                    path="/sysadmin/product-pricing-models"
                    element={
                      <SysAdminLayout
                        Component={ProductPricingModels}
                        PageTitle="Product Pricing Models"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/product-pricing-models/:modelId"
                    element={
                      <SysAdminLayout
                        Component={EditProductPricingModel}
                        PageTitle="Edit Pricing Model"
                        breadcrumbs={[{ label: "Product Pricing Models", href: "/sysadmin/product-pricing-models" }]}
                      />
                    }
                  />
                  {/* Sales Dashboard */}
                  <Route
                    path="/sales"
                    element={
                      <WithLayout
                        Component={SalesDashboard}
                        PageTitle="Sales Dashboard"
                      />
                    }
                  />
                  <Route
                    path="/sales/lead-lookup"
                    element={
                      <WithLayout
                        Component={LeadLookup}
                        PageTitle="Lead Lookup"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/account-expansion"
                    element={
                      <SysAdminLayout
                        Component={ExpansionReport}
                        PageTitle="Account Expansion"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/set-term-date"
                    element={
                      <SysAdminLayout
                        Component={SetTermDate}
                        PageTitle="Set Account Term Date"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/warmup-jobs/:jobId?"
                    element={
                      <SysAdminLayout
                        Component={WarmupJobs}
                        PageTitle="Warmup Jobs"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/sim-farm"
                    element={
                      <SysAdminLayout
                        Component={SimFarmDashboard}
                        PageTitle="SIM Farm Dashboard"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/sim-farm/devices"
                    element={
                      <SysAdminLayout
                        Component={SimFarmGrid}
                        PageTitle="SIM Farm Devices"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/branded-calling-billing"
                    element={
                      <SysAdminLayout
                        Component={BrandedCallingBilling}
                        PageTitle="Branded Calling Billing"
                      />
                    }
                  />
                  <Route
                    path="/sysadmin/reassigned-pools"
                    element={
                      <SysAdminLayout
                        Component={ReassignedPools}
                        PageTitle="Reassigned Pools"
                      />
                    }
                  />
                  <Route
                    path="/admin/cross-accounts"
                    element={
                      <WithLayout
                        Component={AdminAccountsMain}
                        PageTitle="Admin Cross Accounts"
                      />
                    }
                  />
                  <Route
                    path="/admin/client-access"
                    element={
                      <WithLayout
                        Component={AdminAccountsMain}
                        PageTitle="Admin Client Access"
                      />
                    }
                  />
                  <Route
                    path="/admin/sub-accounts"
                    element={
                      <WithLayout
                        Component={AdminAccountsMain}
                        PageTitle="Admin Sub Accounts"
                      />
                    }
                  />
                  <Route
                    path="/admin/intelliscrub"
                    element={
                      <WithLayout
                        Component={AdminIntelliScrub}
                        PageTitle="Admin IntelliScrub Settings"
                      />
                    }
                  />
                  <Route
                    path="/admin/litigator-scrub"
                    element={
                      <WithLayout
                        Component={LitigatorScrubSettings}
                        PageTitle="Litigator Scrub Settings"
                      />
                    }
                  />
                  <Route
                    path="/admin/account"
                    element={
                      <WithLayout
                        Component={AdminAccount}
                        PageTitle="Admin Account Settings"
                      />
                    }
                  />
                  <Route
                    path="/error"
                    element={<WithLayout Component={Error} PageTitle="Error" />}
                  />
                  <Route
                    path="/logocall/enterprise"
                    element={
                      <WithLayout
                        Component={ManageEnterprise}
                        SubLayout={LogoCallLayout}
                        PageTitle="Manage Enterprise"
                      />
                    }
                  />
                  <Route
                    path="/logocall/display-records"
                    element={
                      <WithLayout
                        Component={DisplayRecords}
                        SubLayout={LogoCallLayout}
                        PageTitle="Display Records"
                      />
                    }
                  />
                  <Route
                    path="/logocall/reporting"
                    element={
                      <WithLayout
                        Component={Reporting}
                        SubLayout={LogoCallLayout}
                        PageTitle="LogoCall Reporting"
                      />
                    }
                  />
                  {/* TrustCall Portal */}
                  {/* Reseller entry point — sets sessionStorage flag to hide
                      the top nav, then redirects to the normal TrustCall route */}
                  <Route
                    path="/trustcall/reseller/*"
                    element={<ResellerTrustCallEntry />}
                  />
                  <Route
                    path="/trustcall"
                    element={<Navigate to="/trustcall/dashboard" replace />}
                  />
                  <Route
                    path="/trustcall/dashboard"
                    element={
                      <WithLayout
                        Component={TrustCallDashboard}
                        SubLayout={TrustCallLayout}
                        PageTitle="Dashboard"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/phones"
                    element={
                      <WithLayout
                        Component={TrustCallActivePhones}
                        SubLayout={TrustCallLayout}
                        PageTitle="Current Numbers"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/phones/add"
                    element={
                      <WithLayout
                        Component={TrustCallPhonesAdd}
                        SubLayout={TrustCallLayout}
                        PageTitle="Add Numbers"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/phones/bulk-add"
                    element={
                      <WithLayout
                        Component={TrustCallPhonesBulkAdd}
                        SubLayout={TrustCallLayout}
                        PageTitle="Bulk Add"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/phones/bulk-remove"
                    element={
                      <WithLayout
                        Component={TrustCallPhonesBulkRemove}
                        SubLayout={TrustCallLayout}
                        PageTitle="Bulk Remove"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/archive"
                    element={
                      <WithLayout
                        Component={TrustCallArchivedPhones}
                        SubLayout={TrustCallLayout}
                        PageTitle="Archive"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/scan"
                    element={
                      <WithLayout
                        Component={TrustCallAdHocScan}
                        SubLayout={TrustCallLayout}
                        PageTitle="TrustCall Ad-hoc Scan"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/settings"
                    element={<Navigate to="/trustcall/settings/notifications" replace />}
                  />
                  <Route
                    path="/trustcall/settings/notifications"
                    element={
                      <WithLayout
                        Component={NotificationSettings}
                        SubLayout={TrustCallLayout}
                        PageTitle="Notifications"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/settings/webhooks"
                    element={
                      <WithLayout
                        Component={WebhookSettings}
                        SubLayout={TrustCallLayout}
                        PageTitle="Webhooks"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/settings/legal-entities"
                    element={
                      <WithLayout
                        Component={SysadminLegalEntityList}
                        SubLayout={TrustCallLayout}
                        PageTitle="Legal Entities"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/settings/sysadmin"
                    element={
                      <WithLayout
                        Component={SysadminSysadminTools}
                        SubLayout={TrustCallLayout}
                        PageTitle="Sysadmin Tools"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/help"
                    element={<Navigate to="/trustcall/help/user-guide" replace />}
                  />
                  <Route
                    path="/trustcall/help/user-guide"
                    element={
                      <WithLayout
                        Component={TrustCallUserGuide}
                        SubLayout={TrustCallLayout}
                        PageTitle="User Guide"
                      />
                    }
                  />
                  <Route
                    path="/trustcall/help/api-docs"
                    element={
                      <WithLayout
                        Component={TrustCallApiDocs}
                        SubLayout={TrustCallLayout}
                        PageTitle="API Documentation"
                      />
                    }
                  />

                  <Route
                    path="/help/new"
                    element={
                      <WithLayout
                        Component={NewTicket}
                        PageTitle="Create a new ticket"
                      />
                    }
                  />
                  <Route
                    path="help/tickets"
                    element={
                      <WithLayout
                        Component={ListTickets}
                        PageTitle="My Ticket"
                      />
                    }
                  />
                  <Route
                    path="/help/ticket/:ticketId"
                    element={
                      <WithLayout
                        Component={TicketDetails}
                        PageTitle="Ticket Details"
                      />
                    }
                  />
                  <Route
                    path="/help/docs"
                    element={
                      <WithLayout Component={Docs} PageTitle="Documentation" />
                    }
                  />{" "}
                  <Route
                    path="/billing/pay/:invoiceId"
                    element={
                      <WithLayout
                        Component={PayInvoiceOnline}
                        Layout={PublicLayout} // remove top nav bar since served via iframe
                        PageTitle="Pay Invoice Online"
                      />
                    }
                  />
                  <Route
                    path="/billing/ach-info"
                    element={
                      <WithLayout
                        Component={ACHInfo}
                        PageTitle="ACH Payment Information"
                      />
                    }
                  />
                  <Route
                    path="/version"
                    element={
                      <WithLayout
                        Component={Version}
                        PageTitle="Version Information"
                      />
                    }
                  />{" "}
                  {/* Self-serve signup wizard (DNC-343) — public route */}
                  <Route
                    path="/signup"
                    element={<SignupWizard fullPage />}
                  />
                  <Route
                    path="*"
                    element={
                      <WithLayout
                        Component={NotFound}
                        PageTitle="Page Not Found"
                      />
                    }
                  />
                </Routes>
              </RouteChangeListener>
            </BrowserRouter>
            </QueryClientProvider>
          </HelmetProvider>
        </div>
      </>
    </AppInsightsErrorBoundary>
  );
}

export default App;
